import { BLOCKING, COLLISION_CELL, FOOT_HALF_WIDTH, FOOT_HEIGHT, canStand, type CollisionGrid } from "./step.ts";

/**
 * Where a character is put back on its feet when the spot it names cannot be stood on, such as a saved spot
 * that a newer collision grid walls in or leaves outside the map (ADR-013 amendment "where a character
 * stands"). The search runs over the lattice of cell corners, every position whose coordinates are whole
 * multiples of a collision cell, and asks `canStand` of each, so it holds whatever the feet box's size
 * against a cell's.
 */

/**
 * A feet position in sixteenths of a tile.
 */
export interface FeetPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * Whether the feet box at a position lies wholly inside a grid, so no part of it reads the value the grid
 * gives beyond its edges.
 *
 * @param x Feet centre x in sixteenths.
 * @param y Feet line y in sixteenths.
 * @param grid The collision grid.
 * @returns True when every cell the box overlaps is one of the grid's own.
 */
export function feetInside(x: number, y: number, grid: CollisionGrid): boolean {
  return x - FOOT_HALF_WIDTH >= 0 && x + FOOT_HALF_WIDTH <= grid.width * COLLISION_CELL && y - FOOT_HEIGHT >= 0 && y <= grid.height * COLLISION_CELL;
}

/**
 * The nearest lattice position to a point where the feet box lies inside the grid and stands, searched
 * within `radius` lattice steps of the lattice position nearest the point along each axis. Distance is
 * measured from the point itself, and positions at the same distance are taken in row order, then column
 * order, so the answer depends on nothing but the grid and the point. A point past the grid's edge is
 * brought back inside it when the radius reaches that far.
 *
 * @param x Feet centre x in sixteenths; may lie outside the grid.
 * @param y Feet line y in sixteenths; may lie outside the grid.
 * @param grid The collision grid.
 * @param radius How many lattice steps, each one collision cell, to search from the point along each axis.
 * @param mask Collision bits that block; `BLOCKING` by default.
 * @returns The position, or null when none within the radius can be stood on.
 */
export function nearestStandable(x: number, y: number, grid: CollisionGrid, radius: number, mask: number = BLOCKING): FeetPosition | null {
  const column = Math.round(x / COLLISION_CELL);
  const row = Math.round(y / COLLISION_CELL);
  const left = Math.max(column - radius, Math.ceil(FOOT_HALF_WIDTH / COLLISION_CELL));
  const right = Math.min(column + radius, Math.floor((grid.width * COLLISION_CELL - FOOT_HALF_WIDTH) / COLLISION_CELL));
  const top = Math.max(row - radius, Math.ceil(FOOT_HEIGHT / COLLISION_CELL));
  const bottom = Math.min(row + radius, grid.height);
  let best: FeetPosition | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let step = top; step <= bottom; step += 1) {
    const py = step * COLLISION_CELL;
    for (let across = left; across <= right; across += 1) {
      const px = across * COLLISION_CELL;
      const dx = px - x;
      const dy = py - y;
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance && canStand(px, py, grid, mask)) {
        best = { x: px, y: py };
        bestDistance = distance;
      }
    }
  }
  return best;
}
